<!-- collectionOperationDashboard/index.vue -->
<template>
  <div class="collection-dashboard">
    <!-- 顶 部信 息 -->
    <div class="hero">
      <div class="hero-copy">
        <span class="hero-mark">FM</span>
        <div>
          <div class="eyebrow">FM OPERATIONS CONTROL TOWER</div>
          <h1>{{ $t('collectionOperationDashboard.pageTitle') }}</h1>
          <p>{{ $t('collectionOperationDashboard.pageSubtitle') }}</p>
        </div>
      </div>
      <div class="hero-meta">
        <span class="updated">
          {{ $t('collectionOperationDashboard.dataUpdatedAt') }} {{ updatedTime }}
        </span>
        <span class="sp-badge">{{ $t('collectionOperationDashboard.coverageBadge') }}</span>
      </div>
    </div>

    <!-- 全局筛选 -->
    <section class="filters">
      <div class="filter-head">
        <div class="filter-title">
          <span class="filter-symbol">⌁</span>
          <div>
            <strong>{{ $t('collectionOperationDashboard.filterTitle') }}</strong>
            <small>{{ $t('collectionOperationDashboard.filterDesc') }}</small>
          </div>
        </div>
        <div class="quick">
          <el-button
            v-for="item in quickRanges"
            :key="item.value"
            :class="{ active: quickRange === item.value }"
            size="mini"
            @click="handleQuickRange(item.value)"
          >
            {{ item.label }}
          </el-button>
        </div>
      </div>

      <el-form :model="filters" class="filter-grid" label-position="top">
        <div class="date-range">
          <label>{{ $t('collectionOperationDashboard.timeLabel') }}</label>
          <el-date-picker
            v-model="filters.dateRange"
            :end-placeholder="$t('GlobalSetObj.endDate')"
            :range-separator="$t('GlobalSetObj.to')"
            :start-placeholder="$t('GlobalSetObj.startDate')"
            size="small"
            type="daterange"
            value-format="yyyy-MM-dd"
          />
        </div>

        <el-form-item :label="$t('GlobalSetObj.client')">
          <el-select
            v-model="filters.client"
            :placeholder="$t('collectionOperationDashboard.allClients')"
            clearable
            filterable
            size="small"
          >
            <el-option
              v-for="item in clientOptions" :key="item" :label="item"
              :value="item"
            />
          </el-select>
        </el-form-item>

        <el-form-item :label="$t('GlobalSetObj.serviceTypes')">
          <el-select
            v-model="filters.service"
            :placeholder="$t('collectionOperationDashboard.allServices')"
            clearable
            filterable
            size="small"
          >
            <el-option
              v-for="item in serviceOptions" :key="item" :label="item"
              :value="item"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="UF">
          <el-select
            v-model="filters.uf"
            :placeholder="$t('collectionOperationDashboard.allUf')"
            clearable
            filterable
            size="small"
          >
            <el-option
              v-for="item in ufOptions"
              :key="item.id"
              :label="item.label"
              :value="item.label"
            />
          </el-select>
        </el-form-item>

        <el-form-item :label="$t('collectionCenter.city')">
          <el-select
            v-model="filters.city"
            :loading="cityLoading"
            :placeholder="$t('collectionOperationDashboard.cityPlaceholder')"
            :remote-method="handleCitySearch"
            clearable
            filterable
            remote
            size="small"
          >
            <el-option-group
              v-for="group in cityOptions"
              :key="group.label"
              :label="cityGroupLabel"
            >
              <el-option
                v-for="item in group.options"
                :key="`${group.label}-${item.cityName}`"
                :label="item.cityName"
                :value="item.cityName"
              />
            </el-option-group>
          </el-select>
        </el-form-item>

        <el-form-item label="HUB">
          <el-select
            v-model="filters.hub"
            :placeholder="$t('collectionOperationDashboard.allHub')"
            clearable
            filterable
            size="small"
          >
            <el-option
              v-for="item in hubOptions"
              :key="item.id"
              :label="item.name"
              :value="item.id"
            />
          </el-select>
        </el-form-item>

        <div class="actions">
          <el-button size="small" type="primary" @click="handleQuery">
            {{ $t('GlobalSetObj.search') }}
          </el-button>
          <el-button size="small" @click="handleReset">
            {{ $t('GlobalSetObj.reset') }}
          </el-button>
        </div>
      </el-form>
    </section>

    <!-- 巴西各州 客户揽收分布 -->
    <BrazilMap ref="brazilMapRef" :filters="filters" />

    <!-- 揽收 Coleta -->
    <CollectionOverview ref="collectionRef" :filters="filters" @time-update="handleTimeUpdate" />

    <!-- 按UF统计揽收表现 -->
    <UfPerformance ref="ufRef" :filters="filters" />

    <!-- 城市明细 -->
    <CityDetail ref="cityRef" :filters="filters" />
  </div>
</template>

<script>
import BrazilMap from './component/BrazilMap.vue';
import CollectionOverview from './component/CollectionOverview.vue';
import UfPerformance from './component/UfPerformance.vue';
import CityDetail from './component/CityDetail.vue';
import { getCepCities, getCepStates, getTransitCenterAll } from './api';
import dayjs from 'dayjs';

// 日期格式：yyyy-MM-dd
const DATE_FORMAT = 'YYYY-MM-DD';

export default {
  name: 'CollectionOperationDashboard',
  components: {
    BrazilMap,
    CollectionOverview,
    UfPerformance,
    CityDetail
  },
  data() {
    // 进页面时默认选中当天
    const today = dayjs().format(DATE_FORMAT);
    return {
      updatedTime: '2026-08-06 18:32 BRT',

      // 筛选条件
      filters: {
        dateRange: [today, today],
        client: '',
        service: '',
        uf: '',
        city: '',
        hub: '',
        vehicle: '',
        driver: ''
      },
      quickRange: 'today',

      // 下拉选项
      clientOptions: ['SHEIN', 'TEMU', 'TikTok', 'B2C'],
      serviceOptions: ['W2D', 'D2D', 'SFS'],
      ufOptions: [],
      // 城市下拉选项（/cep/cities 服务端搜索：未选州全局搜，选中州仅搜该州）
      cityOptions: [],
      cityLoading: false,
      // 最近一次远程搜索关键词：用于清空已选城市后判断是否需要恢复全量候选
      cityKeyword: '',
      // 远程搜索防抖计时器
      citySearchTimer: null,
      hubOptions: [],
      vehicleOptions: ['BRA2A19', 'FMS4D88', 'QWE9B12', 'BRA8K44'],
      driverOptions: ['Carlos Silva', 'João Santos', 'Ana Costa', 'Rafael Lima']
    };
  },
  computed: {
    // 快捷时间区间文案随语言切换实时更新（切换语言不会刷新页面）
    quickRanges() {
      return [
        { label: this.$t('collectionCenter.sameDay'), value: 'today' },
        { label: this.$t('collectionCenter.yesterday'), value: 'yesterday' },
        { label: this.$t('collectionCenter.nearlyAWeek'), value: 'week' },
        { label: this.$t('collectionCenter.sameMonth'), value: 'month' }
      ];
    },
    // 城市下拉分组标题：已选州展示州代码，否则展示「城市」
    cityGroupLabel() {
      return this.filters.uf || this.$t('collectionCenter.city');
    }
  },
  watch: {
    // 选中或清空 UF 时，城市下拉同步联动：切换搜索范围（全局 <-> 该州）
    'filters.uf'() {
      this.filters.city = '';
      this.cityKeyword = '';
      this.loadCityOptions();
    },
    // 已选城市被清空（点 x / 重置）时恢复全量候选：
    // 远程搜索会把 cityOptions 收敛成关键词结果，不重拉的话下拉里只剩上次搜到的那个城市
    // （2026-10-26 测试反馈：删除已选城市后再查询，选不到其他城市）
    'filters.city'(val) {
      if (!val && this.cityKeyword) {
        this.cityKeyword = '';
        this.loadCityOptions();
      }
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.handleQuery();
    });
    // 城市下拉初始加载（默认全局前50条城市）
    this.loadCityOptions();
    this.getHubList();
    this.getAddress();
  },
  methods: {
    // HUB下拉：仅转运中心（后端固定 site_type=3），不传status=含停用
    // 需求原文：默认选择转运中心……默认展示所有hub，关闭的hub只会查询不到数据
    getHubList() {
      this.hubOptions = [];
      getTransitCenterAll().then((res) => {
        if (res.code === 200) {
          (res.data || []).forEach((data) => {
            this.hubOptions.push({ id: data.id, name: data.name });
          });
        }
      });
    },
    // 州（巴西邮政DNE州缩写列表，服务端带缓存）
    getAddress() {
      getCepStates()
        .then((res) => {
          if (res.code === 200) {
            const list = res.data || [];
            this.ufOptions = list.map((code) => ({ id: code, label: code }));
          }
        })
        .catch((error) => {
          console.error('[FM揽收看板] 州列表加载失败：', error);
        });
    },
    handleQuickRange(value) {
      this.quickRange = value;
      const now = dayjs();
      let startDate = now;

      switch (value) {
        case 'yesterday':
          startDate = now.subtract(1, 'day');
          this.filters.dateRange = [startDate.format(DATE_FORMAT), startDate.format(DATE_FORMAT)];
          break;
        case 'week':
          startDate = now.subtract(7, 'day');
          this.filters.dateRange = [startDate.format(DATE_FORMAT), now.format(DATE_FORMAT)];
          break;
        case 'month':
          startDate = now.startOf('month');
          this.filters.dateRange = [startDate.format(DATE_FORMAT), now.format(DATE_FORMAT)];
          break;
        default:
          this.filters.dateRange = [now.format(DATE_FORMAT), now.format(DATE_FORMAT)];
      }
      this.handleQuery();
    },

    // 根据页面筛选生成接口公共查询参数（platform/serviceType/stateCode/cityName/siteIds）
    buildQueryParams() {
      const [startDate, endDate] = this.filters.dateRange || [];
      return {
        startDate: startDate || '',
        endDate: endDate || '',
        platform: this.filters.client || '',
        serviceType: this.filters.service || '',
        stateCode: this.filters.uf || '',
        cityName: this.filters.city || '',
        // HUB 下拉对应站点，接口入参为 siteIds 数组；未选择时传空数组表示不限
        siteIds: this.filters.hub ? [this.filters.hub] : []
      };
    },

    // 通知各子组件按最新筛选条件拉取真实接口数据
    async handleQuery() {
      const params = this.buildQueryParams();
      console.log('[FM揽收看板] 公共查询参数：', params);
      const tasks = ['brazilMapRef', 'collectionRef', 'ufRef', 'cityRef']
        .map((name) => {
          const child = this.$refs[name];
          return child && typeof child.fetchData === 'function' ? child.fetchData(params) : null;
        })
        .filter(Boolean);
      try {
        await Promise.all(tasks);
      } catch (error) {
        // 子组件/拦截器已负责错误提示，这里仅记录
        console.error('[FM揽收看板] 部分数据加载失败：', error);
      }
    },

    // 重置各子组件的本地状态（城市明细分页/搜索词、地图点选的州等）
    resetChildren() {
      ['brazilMapRef', 'collectionRef', 'ufRef', 'cityRef'].forEach((name) => {
        const child = this.$refs[name];
        if (child && typeof child.reset === 'function') {
          child.reset();
        }
      });
    },

    handleReset() {
      const today = dayjs().format(DATE_FORMAT);
      this.filters = {
        dateRange: [today, today],
        client: '',
        service: '',
        uf: '',
        city: '',
        hub: '',
        vehicle: '',
        driver: ''
      };
      this.quickRange = 'today';
      // 先清空各子组件本地状态，再统一按重置后的条件重新查询
      this.resetChildren();
      this.handleQuery();
    },

    // 城市下拉远程搜索（Element remote-method）：防抖300ms后调 /cep/cities
    // 服务端包含匹配；未选州时全局搜（产品要求不选州也能直接搜城市）
    // 关键词先去重音：/cep/cities 对带重音的输入匹配不到（服务端只归一化了列没归一化输入，
    // 葡语输入法用户敲 ã/é 会搜出空），前端兜底转无重音再查，服务端修复后依然兼容
    handleCitySearch(keyword) {
      clearTimeout(this.citySearchTimer);
      this.citySearchTimer = setTimeout(() => {
        const kw = this.stripAccents(String(keyword || '').trim());
        // 记录本次搜索词：清空已选城市时据此决定要不要把候选恢复成全量
        this.cityKeyword = kw;
        this.loadCityOptions(kw);
      }, 300);
    },

    // 拉丁字母去重音：ã->a é->e ç->c
    // Unicode NFD 把带重音字母分解成「基础字母+变音符号组合字符(U+0300-U+036F)」，去掉组合字符即得无重音形式
    // 注意：仅用于搜索关键词归一化；选中后传给看板的 cityName 必须保持接口返回的原名（带重音），否则与Doris对不上
    stripAccents(str) {
      return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    },

    // 城市下拉：/cep/cities 服务端搜索，state可选（未选州=全局）；size=200为接口上限，不输关键词时多给些默认选项
    async loadCityOptions(cityKeyword = '') {
      this.cityLoading = true;
      try {
        const params = {
          state: this.filters.uf || '',
          city: cityKeyword || '',
          size: 200
        };
        const res = await getCepCities(params);
        const list = Array.isArray(res && res.data) ? res.data : [];
        const label = this.filters.uf || this.$t('collectionCenter.city');
        this.cityOptions = [{ label, options: list.map((name) => ({ cityName: name })) }];
      } catch (error) {
        console.error('[FM揽收看板] 城市下拉加载失败：', error);
      } finally {
        this.cityLoading = false;
      }
    },

    handleTimeUpdate(time) {
      if (time) {
        this.updatedTime = time;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import './index.scss';
</style>
